import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import GenerationCard from './GenerationCard';
import { 
  useUserGenerations, 
  useDuplicateGeneration, 
  useDeleteGeneration, 
  useGeneratePublicUrl 
} from '@/hooks/useGenerations';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const GenerationsList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'survey' | 'conversation'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: generations = [], isLoading } = useUserGenerations(
    user?.id,
    typeFilter === 'all' ? undefined : typeFilter
  );

  const duplicateGeneration = useDuplicateGeneration();
  const deleteGeneration = useDeleteGeneration();
  const generatePublicUrl = useGeneratePublicUrl();

  const filteredGenerations = generations.filter(generation => {
    const matchesSearch = !searchQuery || 
      generation.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || generation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const surveyGenerations = filteredGenerations.filter(g => g.generation_type === 'survey');
  const conversationGenerations = filteredGenerations.filter(g => g.generation_type === 'conversation');

  const handleView = (generation: any) => {
    navigate(`${prefix}/tools/${generation.tool_id}/results?generationId=${generation.id}&mode=view`);
  };

  const handleEdit = (generation: any) => {
    navigate(`${prefix}/tools/${generation.tool_id}/results?generationId=${generation.id}&mode=edit`);
  };

  const handleDuplicate = (id: string) => {
    duplicateGeneration.mutate(id);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteGeneration.mutate(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleShare = (id: string) => {
    generatePublicUrl.mutate(id);
  };

  const getStats = () => {
    const total = generations.length;
    const surveys = generations.filter(g => g.generation_type === 'survey').length;
    const conversations = generations.filter(g => g.generation_type === 'conversation').length;
    const totalViews = generations.reduce((sum, g) => sum + g.view_count, 0);
    const totalShares = generations.reduce((sum, g) => sum + g.share_count, 0);

    return { total, surveys, conversations, totalViews, totalShares };
  };

  const stats = getStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Generations</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Survey Tools</p>
                <p className="text-2xl font-bold">{stats.surveys}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversations</p>
                <p className="text-2xl font-bold">{stats.conversations}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                <p className="text-2xl font-bold">{stats.totalViews}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Generations</h2>
          <p className="text-muted-foreground">Manage your created surveys and conversation templates</p>
        </div>
        <Button onClick={() => navigate(prefix)} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Create New
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search generations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Generations Tabs */}
      <Tabs value={typeFilter} onValueChange={(value: any) => setTypeFilter(value)}>
        <TabsList>
          <TabsTrigger value="all">All ({filteredGenerations.length})</TabsTrigger>
          <TabsTrigger value="survey">
            Surveys ({surveyGenerations.length})
          </TabsTrigger>
          <TabsTrigger value="conversation">
            Conversations ({conversationGenerations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {filteredGenerations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No generations found</h3>
                <p className="text-muted-foreground text-center mb-4">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Create your first generation to get started'
                  }
                </p>
                <Button onClick={() => navigate('/')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Generation
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGenerations.map((generation) => (
                <GenerationCard
                  key={generation.id}
                  generation={generation}
                  onView={() => handleView(generation)}
                  onEdit={() => handleEdit(generation)}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onShare={handleShare}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="survey" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {surveyGenerations.map((generation) => (
              <GenerationCard
                key={generation.id}
                generation={generation}
                onView={() => handleView(generation)}
                onEdit={() => handleEdit(generation)}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onShare={handleShare}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="conversation" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conversationGenerations.map((generation) => (
              <GenerationCard
                key={generation.id}
                generation={generation}
                onView={() => handleView(generation)}
                onEdit={() => handleEdit(generation)}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onShare={handleShare}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Generation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this generation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GenerationsList;